import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { courseFppqQuestions } from 'dsoapi';

const FloridaPermitPracticeQuestions = ({ setTitle = () => {} }) => {
  const { uceId } = useParams();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [score, setScore] = useState(0);
  const [totalAnswered, setTotalAnswered] = useState(0);
  const [answeredQuestions, setAnsweredQuestions] = useState(new Set());

  setTitle('Florida Permit Practice Prep Test');

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await courseFppqQuestions(uceId);
        setQuestions(response.data.questions || []);
      } catch (err) {
        console.error('Error fetching questions:', err);
        setError('Failed to load questions. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchQuestions();
  }, [uceId]);

  const handleAnswerSelect = (answerId) => {
    setSelectedAnswers((prev) => ({
      ...prev,
      [currentQuestionIndex]: answerId,
    }));
    const currentQuestion = questions[currentQuestionIndex];
    const selectedAnswerObj = currentQuestion.answers.find(
      (a) => a.id === answerId
    );

    if (selectedAnswerObj) {
      // Only increment score and total if this question hasn't been answered before
      if (!answeredQuestions.has(currentQuestionIndex)) {
        setTotalAnswered((prev) => prev + 1);
        setAnsweredQuestions(
          (prev) => new Set([...prev, currentQuestionIndex])
        );

        if (selectedAnswerObj.correct) {
          setScore((prev) => prev + 1);
        }
      }
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((prev) => prev - 1);
    }
  };

  const handleSkipQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
    }
  };

  const resetQuiz = () => {
    setCurrentQuestionIndex(0);
    setSelectedAnswers({});
    setScore(0);
    setTotalAnswered(0);
    setAnsweredQuestions(new Set());
  };

  if (loading) {
    return (
      <div className="ui container" style={{ marginTop: '2rem' }}>
        <div className="ui active centered inline loader"></div>
        <div className="ui center aligned header" style={{ marginTop: '1rem' }}>
          Loading questions...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ui container" style={{ marginTop: '2rem' }}>
        <div className="ui negative message">
          <div className="header">Error</div>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="ui container" style={{ marginTop: '2rem' }}>
        <div className="ui info message">
          <div className="header">No Questions Available</div>
          <p>No practice questions are available at this time.</p>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];
  const progressPercentage =
    ((currentQuestionIndex + 1) / questions.length) * 100;
  const scorePercentage = totalAnswered > 0 ? (score / totalAnswered) * 100 : 0;

  return (
    <div
      className="ui container"
      style={{ marginTop: '2rem', maxWidth: '800px' }}
    >
      {/* Header */}
      <div className="ui center aligned header">
        <h1>Florida Permit Practice Prep Test</h1>
      </div>

      {/* Progress and Score */}
      <div className="ui segment">
        <div className="ui two column grid">
          <div className="column" style={{ textAlign: 'center' }}>
            <div className="ui statistic">
              <div className="value">
                {currentQuestionIndex + 1} / {questions.length}
              </div>
              <div className="label">Question</div>
            </div>
          </div>
          <div className="column" style={{ textAlign: 'center' }}>
            <div className="ui statistic">
              <div className="value">{Math.round(scorePercentage)}%</div>
              <div className="label">
                Score ({score}/{totalAnswered})
              </div>
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="ui indicating progress" style={{ marginTop: '1rem' }}>
          <div
            className="bar"
            style={{ width: `${progressPercentage}%` }}
          ></div>
        </div>

        {/* Reset Button */}
        <div style={{ marginTop: '1rem', textAlign: 'center' }}>
          <button className="ui button" onClick={resetQuiz}>
            <i className="refresh icon"></i>
            Reset Quiz
          </button>
        </div>
      </div>

      {/* Question Card */}
      <div
        className="ui raised segment"
        style={{ marginTop: '2rem', minHeight: '450px' }}
      >
        <div className="ui header">
          <h2>Question {currentQuestionIndex + 1}</h2>
        </div>

        <div className="content">
          <p style={{ fontSize: '1.1rem', lineHeight: '1.6' }}>
            {currentQuestion.text}
          </p>

          {/* Question Image */}
          {currentQuestion.imageUrl && (
            <div style={{ textAlign: 'center', margin: '1rem 0' }}>
              <img
                src={currentQuestion.imageUrl}
                alt="Question"
                className="ui image"
                style={{ maxWidth: '100%', maxHeight: '300px' }}
              />
            </div>
          )}

          {/* Answer Options */}
          <div className="ui form" style={{ marginTop: '2rem' }}>
            {currentQuestion.answers.map((answer) => {
              const isSelected =
                selectedAnswers[currentQuestionIndex] === answer.id;
              const hasAnswered =
                selectedAnswers[currentQuestionIndex] !== undefined;
              const isCorrectAnswer = answer.correct;

              // Show correct answer for all options when user has answered
              const showCorrect = hasAnswered && isCorrectAnswer;
              const showIncorrect = isSelected && !isCorrectAnswer;

              let answerClass = 'ui button fluid';
              if (isSelected) {
                if (isCorrectAnswer) {
                  answerClass += ' positive';
                } else {
                  answerClass += ' negative';
                }
              } else if (hasAnswered && isCorrectAnswer) {
                answerClass += ' positive';
              }

              return (
                <div key={answer.id} style={{ marginBottom: '0.5rem' }}>
                  <button
                    className={answerClass}
                    onClick={() => handleAnswerSelect(answer.id)}
                    disabled={hasAnswered}
                    style={{
                      textAlign: 'left',
                      padding: '1rem',
                      fontSize: '1rem',
                      lineHeight: '1.4',
                    }}
                  >
                    {answer.text}
                    {showCorrect && (
                      <i
                        className="check circle icon"
                        style={{ float: 'right' }}
                      ></i>
                    )}
                    {showIncorrect && (
                      <i
                        className="times circle icon"
                        style={{ float: 'right' }}
                      ></i>
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Navigation Buttons */}
      <div
        className="ui segment"
        style={{ marginTop: '2rem', minHeight: '80px' }}
      >
        <div className="ui two column grid" style={{ marginTop: '-0.5rem' }}>
          <div className="column">
            <button
              className="ui button"
              onClick={handlePreviousQuestion}
              disabled={currentQuestionIndex === 0}
            >
              <i className="left arrow icon"></i>
              Previous
            </button>
          </div>
          <div className="column" style={{ textAlign: 'right' }}>
            {currentQuestionIndex < questions.length - 1 && (
              <button className="ui button" onClick={handleSkipQuestion}>
                Next Question
                <i className="right arrow icon"></i>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FloridaPermitPracticeQuestions;
